import axios from 'axios';
import { rememberTestOrg } from '../utils/testOrg';

/**
 * Shared post-authentication routine used by BOTH password login and SSO.
 * Stores the token, loads the user, mirrors their org/role into localStorage, and routes by role.
 * Kept in one place so federated (SSO) sign-in behaves identically to password sign-in.
 *
 * Throws if the user profile can't be loaded — callers decide how to surface it.
 */
export async function completeLogin({ token, userName, navigate, i18n }) {
  localStorage.setItem('authToken', token);
  localStorage.setItem('userName', userName);

  const userResponse = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/users/username/${userName}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  const user = userResponse.data;
  if (user.locale && i18n) {
    i18n.changeLanguage(user.locale);
    localStorage.setItem('appLocale', user.locale);
  }

  const authorities = user.authorities || [];
  const isPlatformAdmin = authorities.some((a) => a.name === 'ROLE_PLATFORM_ADMIN');
  const isPlatformUser  = authorities.some((a) => a.name === 'ROLE_PLATFORM_USER');
  const isAdmin = authorities.some((a) => a.name === 'ROLE_ADMIN');
  const role = isPlatformAdmin ? 'ROLE_PLATFORM_ADMIN'
             : isPlatformUser  ? 'ROLE_PLATFORM_USER'
             : isAdmin         ? 'ROLE_ADMIN'
             :                   (authorities[0]?.name || 'ROLE_USER');

  localStorage.setItem('role', role);
  localStorage.setItem('organization', user.organization.organizationName);
  localStorage.setItem('orgId', String(user.organization.id));
  // Internal test tenants have every server-side payment wall bypassed; mirror the tag so the
  // billing screens can say so instead of pitching plans the org will never be charged for.
  rememberTestOrg(user);
  const orgRole = user.orgRole || '';
  if (orgRole) localStorage.setItem('orgRole', orgRole);

  if (user.forcePasswordChange) {
    navigate('/change-password-required');
    return;
  }
  if (isPlatformAdmin || isPlatformUser) {
    navigate('/adminHome');
  } else if (orgRole === 'CISO_RESELLER') {
    navigate('/dashboard/reseller');
  } else if (orgRole === 'CPA_ADMIN' || orgRole === 'CPA_AUDITOR') {
    navigate('/dashboard/cpa');
  } else {
    // The dashboard's home route decides the landing (GRC home, or the AML Program for an org
    // whose only enrolled frameworks are AML ones).
    navigate('/dashboard');
  }
}

/** Read the `sub` (username) claim from a JWT without verifying it (client-side display only). */
export function jwtSubject(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(decodeURIComponent(escape(atob(part))));
    return payload.sub || null;
  } catch {
    return null;
  }
}

/** Parse the query string out of a HashRouter URL (#/path?a=b) — window.location.search is empty there. */
export function hashQuery() {
  const h = window.location.hash || '';
  const qi = h.indexOf('?');
  return new URLSearchParams(qi >= 0 ? h.substring(qi + 1) : '');
}
